/**
 * Filter drawer for the Print Assets (Files) view.
 *
 * Filter fields (per Figma design):
 * - Organizations (SUPER_ADMIN only)
 * - File Status (multiselect — Awaiting Input, Enqueued, Failure, Invalid, Processing, Purged, Success)
 */
import { Stack } from "@mantine/core";
import { useForm } from "@mantine/form";
import { GraphQL } from "@rpi/openapi-api";
import { useState } from "react";
import { FilterDrawer } from "../../components/filters/FilterDrawer";
import {
  getInStatusFilter,
  getInitialFilterValues,
  SearchInput,
  toSearchInputArray,
} from "../../configuration/filters/searchInput";
import { MultiselectInput, MultiselectValue } from "../../components/Multiselect";
import { printAssetStatusRender } from "../../utils/searchField.util";
import {
  getCustomerPublicIdFilter,
  getInitialOrganization,
  NO_FILTER_VALUE,
  OrganizationFilter,
} from "../../components/filters/OrganizationFilter";
import { useFilterStyles } from "../../components/filters/filterStyles";
import { useAuthUser } from "../../hooks/useAuthUser";

const filterFormId = "print-assets-filter-form";

type PrintAssetsFilterProps = {
  filters: SearchInput<GraphQL.PrintAssetSearchFields>[];
  onClose: () => void;
  onApply: (filter: SearchInput<GraphQL.PrintAssetSearchFields>[]) => void;
};

export function PrintAssetsFilter({
  isOpened,
  filters,
  onClose,
  onApply,
}: { isOpened: boolean } & PrintAssetsFilterProps) {
  return (
    <FilterDrawer formId={filterFormId} isOpened={isOpened} onClose={onClose}>
      <PrintAssetsFilterContent filters={filters} onClose={onClose} onApply={onApply} />
    </FilterDrawer>
  );
}

function PrintAssetsFilterContent({ filters: initialFilters, onClose, onApply }: PrintAssetsFilterProps) {
  const { classes } = useFilterStyles();
  const user = useAuthUser();

  const { getInputProps, onSubmit, setFieldValue } = useForm({
    initialValues: {
      selectedCustomer: getInitialOrganization(GraphQL.PrintAssetSearchFields.CustomerPublicId, initialFilters),
    },
  });

  const [fileStatusFilter, setFileStatusFilter] = useState<GraphQL.PrintAssetStatus[]>(() =>
    getInitialFilterValues<GraphQL.PrintAssetSearchFields, GraphQL.PrintAssetStatus>(
      GraphQL.PrintAssetSearchFields.Status,
      initialFilters
    )
  );

  const handleApply = (customerPublicId: string) => {
    const filters = toSearchInputArray([
      getCustomerPublicIdFilter(GraphQL.PrintAssetSearchFields.CustomerPublicId, customerPublicId),
      getInStatusFilter(GraphQL.PrintAssetSearchFields.Status, fileStatusFilter),
    ]);

    onApply(filters);
    onClose();
  };

  const handleClear = () => {
    setFieldValue("selectedCustomer", NO_FILTER_VALUE);
    setFileStatusFilter([]);
  };

  return (
    <Stack className={classes.root}>
      <form
        id={filterFormId}
        className={classes.form}
        onSubmit={onSubmit((values) => handleApply(values.selectedCustomer))}
        onReset={handleClear}
      >
        {user.is.SUPER_ADMIN && <OrganizationFilter {...getInputProps("selectedCustomer")} />}
        <MultiselectInput
          label="File Status"
          items={allFileStatuses}
          value={fileStatusFilter.map((value) => ({ id: value, label: printAssetStatusRender(value) }))}
          onValueChange={(newValue) => {
            setFileStatusFilter(newValue.map((value) => value.id));
          }}
          onDelete={(valueToDelete) => {
            setFileStatusFilter((current) => current.filter((status) => status !== valueToDelete.id));
          }}
        />
      </form>
    </Stack>
  );
}

const allFileStatuses: MultiselectValue<GraphQL.PrintAssetStatus>[] = Object.values(GraphQL.PrintAssetStatus).map(
  (status) => ({ id: status, label: printAssetStatusRender(status) })
);
